<script lang="ts">
    import { onMount, tick } from 'svelte'
    import { readable, derived as sderived, type Readable } from 'svelte/store'

    import { Editor } from '@tiptap/core'
    import Highlight from '@tiptap/extension-highlight'
    import Link from '@tiptap/extension-link'
    import TextAlign from '@tiptap/extension-text-align'
    import { TextStyleKit } from '@tiptap/extension-text-style'
    import Typography from '@tiptap/extension-typography'
    import StarterKit from '@tiptap/starter-kit'

    import type { HydriaDocument, IImage } from 'hydria/HydriaDocument'
    import { gettext as _ } from 'hydria/services/gettext'

    import Float from './Float/index'
    import FontSize from './FontSize/index'
    import HydriaImage from './HydriaImage/index'
    import SpanClass from './SpanClass/index'

    import ImageSelect from './ImageSelect.svelte'
    import LinkEdit from './LinkEdit.svelte'
    import Toolbar from './Toolbar.svelte'

    interface Props {
        value?: string
        Document?: HydriaDocument | undefined
        previewName?: string
        initialFocus?: boolean
        customToolbar?: any
        onupdate?: (v: string) => void
    }

    let { value = $bindable(''), Document = undefined, previewName = 'admin', initialFocus = false, customToolbar = $bindable(undefined), onupdate }: Props = $props()

    if (customToolbar == null) customToolbar = Toolbar

    let images: Readable<IImage[]>

    if (Document) {
        images = sderived(Document.attachedImages, ($atts) => $atts)
    } else {
        images = readable([])
    }

    let element: HTMLDivElement | undefined = $state()
    let editor: Editor = $state()

    onMount(() => {
        editor = new Editor({
            element: element,
            extensions: [
                StarterKit,
                Link.configure({
                    openOnClick: false,
                    protocols: ['mailto', 'tel'],
                }),
                Highlight,
                Typography,
                TextStyleKit.configure({
                    fontSize: { types: ['textStyle'] },
                }),
                FontSize,
                SpanClass,
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                    alignments: ['left', 'right', 'center', 'justify', 'balanced'],
                }),
                HydriaImage.configure({
                    inline: true,
                }),
                Float,
            ],
            content: value,
            onTransaction: () => {
                // force re-render so `editor.isActive` works as expected
                editor = editor
            },
        })

        editor.on('update', ({ editor }) => {
            const edValue = editor.getHTML()

            const parser = new DOMParser()
            const dom = parser.parseFromString(edValue, 'text/html')

            const attImgs = Array.from(dom.querySelectorAll('img[aid]'))
            for (const attImg of attImgs) {
                const aid = attImg.getAttribute('aid')
                const image = $images.filter((o) => o.id === aid)[0]
                if (image) {
                    attImg.setAttribute('src', image.url.replace(/,/, '%2C'))
                }
            }

            value = dom.body.innerHTML
            // dispatch('update', value)
            if (onupdate) onupdate(value)
        })

        if (initialFocus) {
            tick().then(() => {
                editor.commands.focus()
            })
        }
    })

    let editImage = $state(false)

    let editLink = $state(false)
</script>

<style>
    .tt {
        min-height: 0;
        /* overflow: hidden; */
        max-height: 100%;
        display: grid;
        grid-template-rows: auto 1fr;
    }
    .ed {
        padding: 0.25rem 0.5rem;
        overflow-x: hidden;
        overflow-y: auto;

        border: var(--bs-border-width) var(--bs-border-style) var(--bs-border-color);
        border-radius: var(--bs-border-radius);

        max-height: 65vh;
    }
    .btn-site :global(.btn) {
        /* sinon cliquer dedans est fucked up */
        -webkit-user-select: initial !important;
        -moz-user-select: initial !important;
        user-select: initial !important;
    }
    .ed > :global(.ProseMirror) {
        outline: 0;
    }

    .ed :global(img.img-position-float-left) {
        float: left;
        margin-right: 1rem;
        margin-bottom: 2ex;
    }
    .ed :global(img.img-position-float-right) {
        float: right;
        margin-left: 1rem;
        margin-bottom: 2ex;
    }
    .ed :global(img.img-position-center) {
        margin-left: auto;
        margin-right: auto;
        display: block;
    }

    .ed :global(img) {
        max-width: 75%;
        max-height: 13rem;

        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.25);
        border-radius: 4px;
        /* Comme '.checkered', pour voir le bg si transparent */
        background-color: var(--checker-bg-color);
        background-image: linear-gradient(45deg, var(--checker-square-color-a) 25%, var(--checker-square-color-b) 25%), linear-gradient(135deg, var(--checker-square-color-a) 25%, var(--checker-square-color-b) 25%), linear-gradient(45deg, var(--checker-square-color-b) 75%, var(--checker-square-color-a) 75%), linear-gradient(135deg, var(--checker-square-color-b) 75%, var(--checker-square-color-a) 75%);
        background-size: calc(2 * var(--checker-square-size)) calc(2 * var(--checker-square-size));
        background-position:
            0 0,
            var(--checker-square-size) 0,
            var(--checker-square-size) calc(-1 * var(--checker-square-size)),
            0 calc(-1 * var(--checker-square-size));
    }
</style>

{#if editImage}
    <ImageSelect {previewName} {editor} close={() => (editImage = false)} {Document} />
{/if}

{#if editLink}
    <LinkEdit {editor} close={() => (editLink = false)} {Document} />
{/if}

<div class="tt">
    {#if editor}
        {@const SvelteComponent = customToolbar}
        <SvelteComponent
            {editor}
            on:edit-image={() => {
                editImage = true
            }}
            on:edit-link={() => {
                editLink = true
            }}
        />
    {/if}
    <div class="ed btn-site scheme scheme-display" bind:this={element}></div>
</div>
